import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Award,
  CircleDot,
  ClipboardList,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  Sparkles,
  Trash2,
  Trophy,
} from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import { CONTAINS_LETTER, CONTAINS_EMOJI } from '../../lib/validators'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { StatusPill } from '../../components/ui/Pills'
import { TextField, TextAreaField } from '../../components/ui/Field'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { EmptyStateBanner } from '../../components/EmptyStateBanner'

/** Company Admin → Gamification. Per-tenant control of the points engine,
 *  rewards, toggles, and the milestone-badge catalogue. The scoring rules
 *  here drive every leaderboard / dashboard / performance figure for this
 *  company; saving them never touches existing points history. */
export function GamificationSettingsPage() {
  const qc = useQueryClient()

  const dataQuery = useQuery({
    queryKey: ['gamification-settings'],
    queryFn: async () => {
      const res = await api.get('/gamification-settings')
      return res.data
    },
  })

  // Editable draft of the SETTINGS half (badges are saved per-row, not via
  // the draft). Primed when the server response lands.
  const [draft, setDraft] = useState(null)
  useEffect(() => {
    if (dataQuery.data?.settings) setDraft(dataQuery.data.settings)
  }, [dataQuery.data])

  const isDirty = useMemo(() => {
    if (!draft || !dataQuery.data?.settings) return false
    return JSON.stringify(draft) !== JSON.stringify(dataQuery.data.settings)
  }, [draft, dataQuery.data])

  const saveMutation = useMutation({
    mutationFn: async (next) => {
      const res = await api.put('/gamification-settings', next)
      return res.data
    },
    onSuccess: (saved) => {
      toast.success('Gamification settings saved successfully.')
      setDraft(saved)
      void qc.invalidateQueries({ queryKey: ['gamification-settings'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  // ── Badge mutations (immediate, not part of the settings draft) ─────
  const badges = dataQuery.data?.badges ?? []
  const [badgeDialog, setBadgeDialog] = useState(null) // null | 'new' | badge
  const [confirmDelete, setConfirmDelete] = useState(null)

  const createBadge = useMutation({
    mutationFn: async (body) =>
      (await api.post('/gamification-settings/badges', body)).data,
    onSuccess: () => {
      toast.success('Badge added successfully.')
      setBadgeDialog(null)
      void qc.invalidateQueries({ queryKey: ['gamification-settings'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })
  const updateBadge = useMutation({
    mutationFn: async ({ id, body }) =>
      (await api.patch(`/gamification-settings/badges/${id}`, body)).data,
    onSuccess: () => {
      toast.success('Badge updated successfully.')
      setBadgeDialog(null)
      void qc.invalidateQueries({ queryKey: ['gamification-settings'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })
  const deleteBadge = useMutation({
    mutationFn: async (id) =>
      (await api.delete(`/gamification-settings/badges/${id}`)).data,
    onSuccess: () => {
      toast.success('Badge deleted successfully.')
      setConfirmDelete(null)
      void qc.invalidateQueries({ queryKey: ['gamification-settings'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  if (dataQuery.isLoading || !draft) {
    return (
      <div className="p-8">
        <p className="text-sm text-[color:var(--color-ink-muted)]">
          Loading gamification settings…
        </p>
      </div>
    )
  }
  if (dataQuery.isError) {
    return (
      <div className="p-8">
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          {extractApiError(dataQuery.error).message}
        </p>
      </div>
    )
  }

  const patch = (key, val) => setDraft((p) => ({ ...p, [key]: val }))
  const patchGroup = (group, key, val) =>
    setDraft((p) => ({ ...p, [group]: { ...p[group], [key]: val } }))

  const on = draft.enabled

  return (
    <div className="pb-12">
      <Breadcrumb current="Gamification" />

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
            <Trophy size={20} className="text-[color:var(--color-accent-strong)]" />
            Gamification
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Configure your company's points, rewards, leaderboard and badges.
          </p>
        </div>
        {/* Persistent Save action — always visible (disabled until there
         *  are unsaved changes) so the save control is never hidden.
         *  Badges save on their own; this saves the settings above. */}
        <div className="flex items-center gap-2 shrink-0">
          {isDirty ? (
            <button
              type="button"
              onClick={() => setDraft(dataQuery.data.settings)}
              disabled={saveMutation.isPending}
              className="inline-flex items-center gap-1.5 px-3 h-10 rounded-lg text-sm font-medium text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <RotateCcw size={14} />
              Reset
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => saveMutation.mutate(draft)}
            disabled={!isDirty || saveMutation.isPending}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg text-sm font-semibold bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={14} />
            {saveMutation.isPending ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* ── Master switches ─────────────────────────────────────────── */}
      <SettingsCard
        icon={Sparkles}
        title="Features"
        subtitle="Turn whole parts of gamification on or off for everyone."
      >
        <div className="divide-y divide-[color:var(--color-rule)]/60">
          <ToggleRow
            label="Gamification"
            hint="Master switch. When off, no new points or badges are awarded. Existing history stays visible — hide the leaderboard separately with the Leaderboards toggle."
            enabled={draft.enabled}
            onChange={(v) => patch('enabled', v)}
          />
          <ToggleRow
            label="Leaderboards"
            hint="Show the ranked leaderboard of points."
            enabled={draft.leaderboardEnabled}
            onChange={(v) => patch('leaderboardEnabled', v)}
            disabled={!on}
          />
          <ToggleRow
            label="Badges"
            hint="Award milestone badges as members hit task thresholds."
            enabled={draft.badgesEnabled}
            onChange={(v) => patch('badgesEnabled', v)}
            disabled={!on}
          />
        </div>
      </SettingsCard>

      {/* ── Rules grid ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <SettingsCard
          icon={Trophy}
          title="Task Priority Points"
          subtitle="Base points earned for completing a task, by its priority."
        >
          <div className="grid grid-cols-2 gap-3">
            <NumberField label="Urgent" value={draft.priorityPoints.urgent} onChange={(v) => patchGroup('priorityPoints', 'urgent', v)} />
            <NumberField label="High" value={draft.priorityPoints.high} onChange={(v) => patchGroup('priorityPoints', 'high', v)} />
            <NumberField label="Medium" value={draft.priorityPoints.normal} onChange={(v) => patchGroup('priorityPoints', 'normal', v)} />
            <NumberField label="Low" value={draft.priorityPoints.low} onChange={(v) => patchGroup('priorityPoints', 'low', v)} />
          </div>
        </SettingsCard>

        <SettingsCard
          icon={CircleDot}
          title="Completion Time Multipliers"
          subtitle="Scale the base points by how early or late the task was finished (100% = full points)."
        >
          <div className="grid grid-cols-2 gap-3">
            <NumberField label="Early Bonus" suffix="%" max={100} value={draft.timeMultipliers.early} onChange={(v) => patchGroup('timeMultipliers', 'early', v)} />
            <NumberField label="On Time" suffix="%" max={100} value={draft.timeMultipliers.onTime} onChange={(v) => patchGroup('timeMultipliers', 'onTime', v)} />
            <NumberField label="Slightly Late" suffix="%" max={100} value={draft.timeMultipliers.slightlyLate} onChange={(v) => patchGroup('timeMultipliers', 'slightlyLate', v)} />
            <NumberField label="Very Late" suffix="%" max={100} value={draft.timeMultipliers.veryLate} onChange={(v) => patchGroup('timeMultipliers', 'veryLate', v)} />
          </div>
        </SettingsCard>

        <SettingsCard
          icon={ClipboardList}
          title="Work Report Points"
          subtitle="Points earned for submitting a work report, by cadence. Awarded once per period on the first submission."
        >
          <div className="grid grid-cols-2 gap-3">
            <NumberField label="Daily Report" value={draft.reportPoints?.daily ?? 0} onChange={(v) => patchGroup('reportPoints', 'daily', v)} />
            <NumberField label="Weekly Report" value={draft.reportPoints?.weekly ?? 0} onChange={(v) => patchGroup('reportPoints', 'weekly', v)} />
            <NumberField label="Monthly Report" value={draft.reportPoints?.monthly ?? 0} onChange={(v) => patchGroup('reportPoints', 'monthly', v)} />
          </div>
        </SettingsCard>

        {/* "Task Approval Points" intentionally hidden — those points only
         *  apply when tasks route through the review/approval chain, which
         *  this product doesn't surface. The backend keeps its defaults so
         *  any future approval still credits the reviewer sensibly. */}
      </div>

      {/* ── Badge management ────────────────────────────────────────── */}
      <SettingsCard
        icon={Award}
        title="Milestone Badges"
        subtitle="Badges members earn for reaching a number of delivered tasks."
        className="mt-4"
        action={
          <Button size="sm" onClick={() => setBadgeDialog('new')}>
            <Plus size={14} /> Add Badge
          </Button>
        }
      >
        {badges.length === 0 ? (
          <EmptyStateBanner
            icon={Trophy}
            title="No badges yet"
            body="Create a badge to reward teammates for hitting task milestones."
            tone="amber"
            compact
          />
        ) : (
          <div className="rounded-lg border border-[color:var(--color-rule)] overflow-hidden">
            <div className="grid grid-cols-[1fr_110px_110px_90px] gap-3 px-4 py-2.5 bg-[color:var(--color-canvas)] text-xs font-semibold uppercase tracking-wider text-[color:var(--color-ink-muted)]">
              <span>Badge</span>
              <span className="text-right">Tasks</span>
              <span className="text-center">Status</span>
              <span className="text-right">Actions</span>
            </div>
            {badges.map((b) => (
              <div
                key={b.id}
                className="grid grid-cols-[1fr_110px_110px_90px] gap-3 px-4 py-3 items-center border-t border-[color:var(--color-rule)]"
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[color:var(--color-ink-strong)] truncate">
                    {b.name}
                  </p>
                  {b.description ? (
                    <p className="text-xs text-[color:var(--color-ink-muted)] truncate">
                      {b.description}
                    </p>
                  ) : null}
                </div>
                <span className="text-right text-sm tabular-nums text-[color:var(--color-ink-strong)]">
                  {b.requiredTaskCount}
                </span>
                <span className="flex justify-center">
                  <StatusPill status={b.isActive ? 'active' : 'inactive'} />
                </span>
                <div className="flex items-center justify-end gap-1.5">
                  <button
                    type="button"
                    onClick={() => setBadgeDialog(b)}
                    title="Edit badge"
                    className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-accent-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(b)}
                    title="Delete Badge"
                    className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-danger-bg)] cursor-pointer"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </SettingsCard>

      {/* Badge create/edit dialog */}
      {badgeDialog ? (
        <BadgeDialog
          initial={badgeDialog === 'new' ? null : badgeDialog}
          saving={createBadge.isPending || updateBadge.isPending}
          existingNames={badges
            .filter((b) => badgeDialog === 'new' || b.id !== badgeDialog.id)
            .map((b) => b.name)}
          existingCounts={badges
            .filter((b) => badgeDialog === 'new' || b.id !== badgeDialog.id)
            .map((b) => b.requiredTaskCount)}
          onClose={() => setBadgeDialog(null)}
          onSubmit={(body) => {
            if (badgeDialog === 'new') createBadge.mutate(body)
            else updateBadge.mutate({ id: badgeDialog.id, body })
          }}
        />
      ) : null}

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Delete Badge"
        message={
          confirmDelete
            ? `Delete the "${confirmDelete.name}" badge? Members who already earned it keep it; it just won't be awarded again.`
            : ''
        }
        confirmLabel="Delete Badge"
        variant="danger"
        confirming={deleteBadge.isPending}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete) deleteBadge.mutate(confirmDelete.id)
        }}
      />
    </div>
  )
}

// ── Badge create/edit dialog ──────────────────────────────────────────
function BadgeDialog({ initial, saving, existingNames = [], existingCounts = [], onClose, onSubmit }) {
  const isEdit = Boolean(initial)
  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [count, setCount] = useState(
    initial?.requiredTaskCount != null ? String(initial.requiredTaskCount) : '',
  )
  const [isActive, setIsActive] = useState(initial?.isActive ?? true)
  const [errors, setErrors] = useState({})

  function submit() {
    const next = {}
    const trimmedName = name.trim()
    const n = Number(count)
    if (!trimmedName) {
      next.name = 'Badge Name is required.'
    } else if (trimmedName.length < 2) {
      next.name = 'Badge Name must be at least 2 characters.'
    } else if (!CONTAINS_LETTER.test(trimmedName)) {
      next.name = 'Badge Name must contain at least one letter.'
    } else if (CONTAINS_EMOJI.test(trimmedName)) {
      next.name = 'Badge Name cannot contain emojis.'
    } else if (
      existingNames.some(
        (existing) => existing.trim().toLowerCase() === trimmedName.toLowerCase(),
      )
    ) {
      // Client-side pre-check; the backend enforces this authoritatively too.
      next.name = 'A badge with this name already exists.'
    }
    if (!count || !Number.isInteger(n) || n < 1) {
      next.requiredTaskCount = 'Enter a whole number of 1 or more.'
    } else if (existingCounts.includes(n)) {
      // Client-side pre-check; the backend enforces this authoritatively too.
      next.requiredTaskCount = 'Another badge already uses this required task count.'
    }
    setErrors(next)
    if (Object.keys(next).length > 0) return
    onSubmit({
      name: trimmedName,
      description: description.trim(),
      requiredTaskCount: n,
      isActive,
    })
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? 'Edit Badge' : 'Add Badge'}
      description="Members earn this badge once they reach the required number of delivered tasks."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} loading={saving}>
            {isEdit ? 'Save Changes' : 'Add Badge'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <TextField
          label="Badge Name"
          required
          placeholder="Enter a Badge Name"
          minLength={2}
          maxLength={60}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={errors.name}
        />
        <TextAreaField
          label="Description"
          rows={2}
          placeholder="Enter a Description"
          maxLength={300}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <TextField
          label="Required Task Count"
          required
          type="number"
          min={1}
          placeholder="Enter the Required Task Count"
          value={count}
          onChange={(e) => setCount(e.target.value)}
          error={errors.requiredTaskCount}
          helperText="Distinct delivered tasks a member must reach to earn this badge."
        />
        <label className="flex items-center gap-2.5 cursor-pointer select-none pt-1">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-4 w-4 cursor-pointer accent-[color:var(--color-accent)]"
          />
          <span className="text-sm text-[color:var(--color-ink-strong)]">
            Active (awarded automatically)
          </span>
        </label>
      </div>
    </Modal>
  )
}

// ── Local primitives (mirrors ReportSchedulePage) ─────────────────────
function SettingsCard({ icon: Icon, title, subtitle, children, action, className = '' }) {
  return (
    <section
      className={
        'rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] ' +
        className
      }
    >
      <div className="flex items-start gap-3 p-4 border-b border-[color:var(--color-rule)]/60">
        <div
          className="h-10 w-10 rounded-lg border border-[color:var(--color-accent-border)] bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] flex items-center justify-center shrink-0"
          aria-hidden="true"
        >
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
            {title}
          </p>
          <p className="text-xs text-[color:var(--color-ink-muted)] mt-0.5">
            {subtitle}
          </p>
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      <div className="p-4">{children}</div>
    </section>
  )
}

function ToggleRow({ label, hint, enabled, onChange, disabled = false }) {
  return (
    <div
      className={
        'flex items-center gap-4 py-3 first:pt-0 last:pb-0 ' +
        (disabled ? 'opacity-50' : '')
      }
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[color:var(--color-ink-strong)]">
          {label}
        </p>
        <p className="text-xs text-[color:var(--color-ink-muted)] mt-0.5">
          {hint}
        </p>
      </div>
      <ToggleSwitch
        enabled={enabled}
        onChange={onChange}
        disabled={disabled}
        srLabel={`${label} enabled`}
      />
    </div>
  )
}

function ToggleSwitch({ enabled, onChange, srLabel, disabled = false }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={srLabel}
      disabled={disabled}
      onClick={() => onChange(!enabled)}
      className={
        'relative inline-flex shrink-0 h-6 w-11 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]/40 ' +
        (disabled ? 'cursor-not-allowed ' : 'cursor-pointer ') +
        (enabled
          ? 'bg-[color:var(--color-accent)]'
          : 'bg-[color:var(--color-rule-strong)]')
      }
    >
      <span
        aria-hidden="true"
        className={
          'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ' +
          (enabled ? 'translate-x-5' : 'translate-x-0.5')
        }
      />
    </button>
  )
}

const INPUT_BASE =
  'w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink)] outline-none transition-colors focus:border-[color:var(--color-accent)] focus:ring-2 focus:ring-[color:var(--color-accent)]/20'

function NumberField({ label, value, onChange, suffix, max }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1.5">
        {label}
      </label>
      <div className="relative">
        <input
          type="number"
          min={0}
          max={max}
          inputMode="numeric"
          value={value}
          onChange={(e) => {
            let next = Number(e.target.value)
            if (!Number.isFinite(next) || next < 0) return
            // Clamp percentages so a value above the cap can't be entered.
            if (max != null && next > max) next = max
            onChange(next)
          }}
          className={INPUT_BASE + (suffix ? ' pr-9' : '')}
        />
        {suffix ? (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[color:var(--color-ink-muted)] pointer-events-none">
            {suffix}
          </span>
        ) : null}
      </div>
    </div>
  )
}
